import { useState, useEffect, useCallback } from 'react';
import { Notification } from '../types/notification';
import { getNotifications } from '../actions/notifications/getNotifications';
import { NOTIFICATION_CONSTANTS } from '../constants/notifications';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to manage system notifications with localStorage-based dismissal
 * 
 * Features:
 * - Fetches notifications from backend
 * - Filters out expired notifications
 * - Tracks dismissed notifications in localStorage with quota protection
 * - Auto-refreshes every 5 minutes
 * - Provides dismiss functionality
 * - Handles localStorage errors gracefully (Safari private mode, quota exceeded)
 */
export function useNotifications() {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  // Get dismissed notification IDs from localStorage
  const getDismissedIds = useCallback((): Set<string> => {
    try {
      const dismissed = localStorage.getItem(NOTIFICATION_CONSTANTS.DISMISSED_STORAGE_KEY);
      return dismissed ? new Set(JSON.parse(dismissed)) : new Set();
    } catch (error) {
      console.error('Failed to read dismissed notifications:', error);
      return new Set();
    }
  }, []);

  // Save dismissed notification IDs to localStorage with quota protection
  const saveDismissedIds = useCallback((ids: Set<string>) => {
    try {
      const idsArray = Array.from(ids);
      
      // Limit storage to last N dismissals to prevent quota issues
      const limitedIds = idsArray.slice(-NOTIFICATION_CONSTANTS.MAX_DISMISSED_STORAGE);
      
      const data = JSON.stringify(limitedIds);
      
      // Safety check: if data exceeds size limit, trim more aggressively
      if (data.length > NOTIFICATION_CONSTANTS.MAX_STORAGE_SIZE_BYTES) {
        const trimmedIds = limitedIds.slice(-50); // Keep only last 50
        localStorage.setItem(NOTIFICATION_CONSTANTS.DISMISSED_STORAGE_KEY, JSON.stringify(trimmedIds));
        console.warn('Trimmed dismissed notifications to prevent storage overflow');
      } else {
        localStorage.setItem(NOTIFICATION_CONSTANTS.DISMISSED_STORAGE_KEY, data);
      }
    } catch (error) {
      // Handle quota exceeded error
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded, clearing old dismissals');
        try {
          localStorage.removeItem(NOTIFICATION_CONSTANTS.DISMISSED_STORAGE_KEY);
          // Try again with just the most recent ID
          const lastId = Array.from(ids).pop();
          if (lastId) {
            localStorage.setItem(NOTIFICATION_CONSTANTS.DISMISSED_STORAGE_KEY, JSON.stringify([lastId]));
          }
        } catch {
          // If still fails, continue without localStorage
          console.error('Cannot use localStorage - dismissals will not persist across sessions');
        }
      } else {
        console.error('Failed to save dismissed notifications:', error);
      }
    }
  }, []);

  // Fetch and filter notifications
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      const allNotifications = await getNotifications();
      const dismissedIds = getDismissedIds();
      
      // Filter out dismissed notifications
      const activeNotifications = allNotifications.filter(
        (n) => !dismissedIds.has(n.id)
      );

      setNotifications(activeNotifications);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [getDismissedIds, isAuthenticated]);

  // Dismiss a notification
  const dismissNotification = useCallback((id: string) => {
    const dismissedIds = getDismissedIds();
    dismissedIds.add(id);
    saveDismissedIds(dismissedIds);

    // Remove from current state
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, [getDismissedIds, saveDismissedIds]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const interval = setInterval(fetchNotifications, NOTIFICATION_CONSTANTS.REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchNotifications, isAuthenticated]);

  return {
    notifications,
    loading,
    dismissNotification,
    refresh: fetchNotifications,
  };
}

